import { useState, useEffect, useCallback } from 'react';
import { userValidationService } from '../services/userValidationService';

/**
 * Custom hook to validate user account status
 * @param {Object} options - Configuration options
 * @param {number} options.checkInterval - Interval in milliseconds to check user status (default: 30000 - 30 seconds)
 * @param {boolean} options.enabled - Whether to enable automatic checking (default: true)
 * @returns {Object} Hook state and methods
 */
export const useUserValidation = (options = {}) => {
  const {
    checkInterval = 30000, // 30 seconds
    enabled = true
  } = options;

  const [isValidating, setIsValidating] = useState(false);
  const [isUserActive, setIsUserActive] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const validateUser = useCallback(async () => {
    if (!enabled) return;

    setIsValidating(true);
    setValidationError(null);

    try {
      const result = await userValidationService.validateUser();
      
      if (result.success) {
        setIsUserActive(result.isActive);
        setLastChecked(new Date());
        
        if (!result.isActive) {
          setValidationError(result.message);
        }
      } else {
        setIsUserActive(false);
        setValidationError(result.message);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('User validation hook error:', error);
      setValidationError('Failed to validate user account');
      setIsUserActive(false);
    } finally {
      setIsValidating(false);
    }
  }, [enabled]);

  // Initial validation on mount
  useEffect(() => {
    validateUser();
  }, [validateUser]);

  // Set up interval for periodic validation
  useEffect(() => {
    if (!enabled || checkInterval <= 0) return;

    const interval = setInterval(validateUser, checkInterval);
    return () => clearInterval(interval);
  }, [validateUser, checkInterval, enabled]);

  return {
    isValidating,
    isUserActive,
    validationError,
    lastChecked,
    validateUser,
    setIsUserActive
  };
};

export default useUserValidation;
